import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as AwardIcon,
  PersonAdd as InviteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { getRfq, updateRfq, assignRfqPartners } from '../../api/rfqs';
import { fetchPartners } from '../../api/partners';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'issued', label: 'Issued' },
  { value: 'in_review', label: 'In Review' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'MMM d, yyyy • h:mm a');
  } catch {
    return value;
  }
};

const toTextList = (value) =>
  Array.isArray(value)
    ? value.join(', ')
    : typeof value === 'string'
      ? value
      : '—';

const RFQDetailPage = () => {
  const { rfqId } = useParams();
  const navigate = useNavigate();

  const [rfq, setRfq] = useState(null);
  const [status, setStatus] = useState('draft');
  const [deadlines, setDeadlines] = useState({
    responseDue: '',
    decisionDate: '',
    dropDate: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);

  const loadRfq = async () => {
    try {
      setLoading(true);
      const response = await getRfq(rfqId);
      const rfqData = response;
      setRfq(rfqData);
      setStatus(rfqData.status);
      setDeadlines({
        responseDue: rfqData.deadlines?.responseDue
          ? new Date(rfqData.deadlines.responseDue).toISOString().slice(0, 10)
          : '',
        decisionDate: rfqData.deadlines?.decisionDate
          ? new Date(rfqData.deadlines.decisionDate).toISOString().slice(0, 10)
          : '',
        dropDate: rfqData.deadlines?.dropDate
          ? new Date(rfqData.deadlines.dropDate).toISOString().slice(0, 10)
          : '',
      });
    } catch (err) {
      console.error('Failed to load RFQ', err);
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to load RFQ. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRfq();
  }, [rfqId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        status,
        deadlines: {
          responseDue: deadlines.responseDue ? new Date(deadlines.responseDue) : undefined,
          decisionDate: deadlines.decisionDate ? new Date(deadlines.decisionDate) : undefined,
          dropDate: deadlines.dropDate ? new Date(deadlines.dropDate) : undefined,
        },
      };

      const updated = await updateRfq(rfqId, payload);
      setRfq(updated);
      toast.success('RFQ updated successfully');
    } catch (err) {
      console.error('Failed to update RFQ', err);
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to update RFQ. Please try again.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openInviteDialog = async () => {
    setInviteDialogOpen(true);
    if (availablePartners.length === 0) {
      try {
        const partnerList = await fetchPartners();
        setAvailablePartners(
          (partnerList || []).map((partner) => ({
            id: partner._id || partner.id,
            name: partner.company?.legalName || partner.legalName || partner.name,
            tier: partner.tier,
          }))
        );
      } catch (err) {
        console.error('Failed to load partners', err);
        toast.error('Unable to load partners');
      }
    }
  };

  const handleInvitePartners = async () => {
    if (!selectedPartnerIds.length) {
      toast.error('Select at least one partner to invite.');
      return;
    }
    try {
      setInviteLoading(true);
      const updated = await assignRfqPartners(rfqId, selectedPartnerIds);
      setRfq(updated);
      toast.success('Partners invited.');
      setInviteDialogOpen(false);
      setSelectedPartnerIds([]);
    } catch (err) {
      console.error('Failed to invite partners', err);
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to invite partners. Please try again.'
      );
    } finally {
      setInviteLoading(false);
    }
  };

  const assignments = useMemo(() => rfq?.assignments || [], [rfq]);

  const renderResponseHistory = (history = []) => {
    if (!history.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No responses yet.
        </Typography>
      );
    }

    return (
      <Stack spacing={1.5}>
        {[...history]
          .sort(
            (a, b) =>
              new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime()
          )
          .map((entry, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle2">
                  {entry.committed
                    ? 'Committed terms'
                    : entry.status === 'declined'
                      ? 'Declined'
                      : 'Negotiation update'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(entry.submittedAt)}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {entry.notes || 'No additional notes provided.'}
              </Typography>
              {entry.terms && (
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Unit cost
                    </Typography>
                    <Typography variant="body2">
                      {entry.terms.unitCost
                        ? `${entry.terms.currency || 'USD'} ${entry.terms.unitCost}`
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Lead time
                    </Typography>
                    <Typography variant="body2">
                      {entry.terms.leadTimeDays
                        ? `${entry.terms.leadTimeDays} days`
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Capacity
                    </Typography>
                    <Typography variant="body2">
                      {entry.terms.minimumUnits || '—'} / {entry.terms.maximumUnits || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">
                      Shipping
                    </Typography>
                    <Typography variant="body2">
                      {entry.terms.shippingTerms || '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="text.secondary">
                      Fulfillment plan
                    </Typography>
                    <Typography variant="body2">
                      {entry.terms.fulfillmentPlan || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          ))}
      </Stack>
    );
  };

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <IconButton onClick={() => navigate('/rfqs')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          RFQ Detail
        </Typography>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : rfq ? (
        <>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {rfq.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rfq.code || rfq.id}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip label={rfq.status} color="primary" />
                  {rfq.brand?.name && <Chip label={rfq.brand.name} variant="outlined" />}
                  {rfq.campaign?.name && <Chip label={`Campaign: ${rfq.campaign.name}`} variant="outlined" />}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<InviteIcon />}
                  onClick={openInviteDialog}
                >
                  Invite partners
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AwardIcon />}
                  onClick={() => {
                    setStatus('awarded');
                    toast('Set status to awarded. Save changes to confirm.');
                  }}
                >
                  Mark as awarded
                </Button>
              </Stack>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  label="Status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  fullWidth
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Response due"
                    type="date"
                    value={deadlines.responseDue}
                    onChange={(event) =>
                      setDeadlines((prev) => ({ ...prev, responseDue: event.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Decision date"
                    type="date"
                    value={deadlines.decisionDate}
                    onChange={(event) =>
                      setDeadlines((prev) => ({ ...prev, decisionDate: event.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Drop date"
                    type="date"
                    value={deadlines.dropDate}
                    onChange={(event) =>
                      setDeadlines((prev) => ({ ...prev, dropDate: event.target.value }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={18} /> : 'Save changes'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Requirements
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Target units
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.production?.targetUnits ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Min / Max units
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.production?.minimumUnits ?? '—'} /{' '}
                  {rfq.requirements?.production?.maximumUnits ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">
                  Size curve
                </Typography>
                <Typography variant="body2">
                  {toTextList(rfq.requirements?.production?.sizeCurve)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Materials
                </Typography>
                <Typography variant="body2">
                  {toTextList(rfq.requirements?.production?.materials)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Processes
                </Typography>
                <Typography variant="body2">
                  {toTextList(rfq.requirements?.production?.processes)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Sustainability
                </Typography>
                <Typography variant="body2">
                  {toTextList(rfq.requirements?.production?.sustainability)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">
                  Compliance
                </Typography>
                <Typography variant="body2">
                  {toTextList(rfq.requirements?.production?.compliance)}
                </Typography>
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Shipping & Fulfillment</Typography>
                <Typography variant="caption" color="text.secondary">
                  Incoterms
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.shipping?.incoterms || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Destination
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.shipping?.destination || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Packaging
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.shipping?.packaging || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Fulfillment
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.fulfillment?.strategy || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Fulfillment notes
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.fulfillment?.notes || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Quality & Compliance</Typography>
                <Typography variant="caption" color="text.secondary">
                  Quality standards
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.quality?.standards || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Notes
                </Typography>
                <Typography variant="body2">
                  {rfq.requirements?.quality?.notes || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Tech Pack & Attachments
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {rfq.techpack?.yaml && (
                <Box>
                  <Typography variant="subtitle2">Raw YAML</Typography>
                  <pre
                    style={{
                      marginTop: 8,
                      background: '#1c1c1c',
                      color: '#f0f0f0',
                      fontSize: 12,
                      borderRadius: 8,
                      padding: 16,
                      overflow: 'auto',
                    }}
                  >
                    {rfq.techpack.yaml}
                  </pre>
                </Box>
              )}
              {(rfq.techpack?.attachments || []).length > 0 && (
                <Box>
                  <Typography variant="subtitle2">Assets</Typography>
                  <Stack spacing={1} mt={1}>
                    {rfq.techpack.attachments.map((asset, index) => (
                      <Button
                        key={index}
                        component="a"
                        href={asset.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                      >
                        {asset.name || asset.url}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Partner Responses
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {assignments.length} invited partner(s)
              </Typography>
            </Stack>

            <Stack spacing={2} sx={{ mt: 2 }}>
              {assignments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No partners invited yet. Use "Invite partners" to add candidates.
                </Typography>
              ) : (
                assignments.map((assignment, index) => {
                  const partner = assignment.partner || {};
                  const name =
                    partner.company?.doingBusinessAs ||
                    partner.company?.legalName ||
                    partner.name ||
                    partner.code ||
                    assignment.partner;
                  return (
                    <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between">
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {name}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={assignment.status}
                              color={
                                assignment.status === 'committed'
                                  ? 'success'
                                  : assignment.status === 'awarded'
                                    ? 'primary'
                                    : assignment.status === 'declined'
                                      ? 'error'
                                      : 'default'
                              }
                              size="small"
                            />
                            {partner.tier && <Chip label={partner.tier} size="small" variant="outlined" />}
                          </Stack>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Invited {formatDateTime(assignment.invitedAt)}
                        </Typography>
                      </Stack>
                      <Divider sx={{ my: 2 }} />
                      {assignment.latestResponse ? (
                        <>
                          <Typography variant="subtitle2">Latest response</Typography>
                          {renderResponseHistory([assignment.latestResponse])}
                          <Divider sx={{ my: 1 }} />
                          <Typography variant="subtitle2">History</Typography>
                          {renderResponseHistory(assignment.history)}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Awaiting response
                        </Typography>
                      )}
                    </Paper>
                  );
                })
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Internal Notes
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {rfq.notes || '—'}
            </Typography>
          </Paper>
        </>
      ) : null}

      <Dialog
        fullWidth
        maxWidth="sm"
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
      >
        <DialogTitle>Invite partners</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Select the partners to notify about this RFQ. They’ll receive the request in their portal immediately.
          </Typography>
          <Stack spacing={1} sx={{ mt: 2 }}>
            {availablePartners.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              availablePartners.map((partner) => {
                const selected = selectedPartnerIds.includes(partner.id);
                return (
                  <Chip
                    key={partner.id}
                    label={`${partner.name || 'Partner'}${partner.tier ? ` • ${partner.tier}` : ''}`}
                    color={selected ? 'primary' : 'default'}
                    onClick={() =>
                      setSelectedPartnerIds((prev) =>
                        prev.includes(partner.id)
                          ? prev.filter((id) => id !== partner.id)
                          : [...prev, partner.id]
                      )
                    }
                    variant={selected ? 'filled' : 'outlined'}
                    sx={{ justifyContent: 'flex-start' }}
                  />
                );
              })
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInvitePartners}
            disabled={inviteLoading}
            startIcon={inviteLoading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Invite
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RFQDetailPage;
